// components/SolucionesSection.tsx

import { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';

import {
    DataTableColumn,
} from '@/presentation/components/common/DataTable';
import { IconButton } from '@/presentation/components/common/IconButton/IconButton';
import { useCongrupo } from '@/presentation/hooks/useCongrupo';
import { useSimple } from '@/presentation/hooks/useSimple';
import { useAuthContext } from '@/presentation';

import { ActionSection } from './ActionSection';
import { CreateDialog } from './CreateActionDialog';
import { DeleteActionDialog } from './DeleteActionDialog';
import { Form } from '@/presentation/components/Forms/Form/Form';
import { formatSmartDateTime } from '@/shared/utils/dateFormaters';
import { formatGroup } from '@/shared/utils/formatters';

interface ActionItem {
    id: number;
    nombre: string;
    usuario: string;
    fecha: Date;
}

interface SolucionesSectionProps {
    incidenteId: string;
}

export function SolucionesSection({
    incidenteId,
}: SolucionesSectionProps) {
    const [createOpen, setCreateOpen] =
        useState(false);
    const { user } = useAuthContext();

    const [deleteOpen, setDeleteOpen] =
        useState(false);

    const [selectedId, setSelectedId] =
        useState<number | null>(null);

    const {
        data: tiposSolucion,
        reload: loadTiposSolucion,
    } = useSimple('luc_tipo_soluciones');

    const { remove } =
        useSimple('luc_soluciones');

    const {
        data: soluciones,
        loadByGroup,
        create,
    } = useCongrupo();

    const loadSoluciones = async () => {
        await loadByGroup(
            'luc_soluciones',
            incidenteId
        );
    };

    useEffect(() => {
        loadSoluciones();
    }, [incidenteId]);

    const optionGroups = useMemo(() => {
        const grupos = new Map<
          number | undefined,
          { value: number; label: string }[]
        >();
    
        for (const Solucion of tiposSolucion) {
          const opciones = grupos.get(Solucion.id_grupo) ?? [];
    
          opciones.push({
            value: Solucion.id,
            label: Solucion.nombre,
          });
    
          grupos.set(Solucion.id_grupo, opciones);
        }
    
        return Array.from(grupos, ([idGrupo, options]) => ({
          label: idGrupo == null ? 'Sin grupo' : formatGroup(idGrupo),
          options,
        }));
      }, [tiposSolucion]);

    const columns: DataTableColumn<ActionItem>[] =
        [
            {
                key: 'nombre',
                header: 'Detalle',
            },
            {
                key: 'usuario',
                header: 'Usuario',
            },
            {
                key: 'fecha',
                header: 'Fecha',
                render: (item) =>
                    formatSmartDateTime(item.fecha),
            },
            {
                key: 'eliminar',
                header: '',
                render: (item) => (
                    <IconButton
                        icon={<Trash2 size={30} />}
                        title="Eliminar"
                        color="danger"
                        onClick={() => {
                            setSelectedId(item.id);
                            setDeleteOpen(true);
                        }}
                    />
                ),
            },
        ];

    const handleOpenCreate = async () => {
        if (tiposSolucion.length === 0) {
            await loadTiposSolucion();
        }

        setCreateOpen(true);
    };

    const handleCreate = async (
        values: Record<string, unknown>
    ) => {
        await create('luc_soluciones', {
            nombre: String(values.nombre),
            grupo: incidenteId,
            usuario: String(user?.id),
            fecha: new Date()
        });

        setCreateOpen(false);
        await loadSoluciones();
    };

    const handleDelete = async () => {
        if (selectedId === null) return;

        await remove(selectedId);

        setDeleteOpen(false);
        setSelectedId(null);

        await loadSoluciones();
    };

    return (
        <>
            <ActionSection
                title="Soluciones"
                buttonLabel="Nueva Solución"
                data={soluciones}
                columns={columns}
                emptyMessage="No hay soluciones cargadas"
                onCreate={handleOpenCreate}
            />

            <CreateDialog
                open={createOpen}
                title="Nueva Solución"
                onClose={() => setCreateOpen(false)}
            >
                <Form
                    fields={[
                        {
                            name: "nombre",
                            type: "select",
                            label: "Solución",
                            optionGroups,
                        },
                    ]}
                    submitLabel="Agregar"
                    onSubmit={handleCreate}
                />
            </CreateDialog>

            <DeleteActionDialog
                open={deleteOpen}
                title="Eliminar solución"
                message="¿Seguro que quiere eliminar la solución?"
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDelete}
            />
        </>
    );
}